/**
 * PTBFFixationListPage — danh sách PTBF Fixations. Đồng bộ ContractListView.
 * Edit/Delete disable khi is_upload=1 (FR-PT-03/04).
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Divider, FormControl, InputLabel, Menu, MenuItem, Popover,
  Select, Stack, Switch, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TextField, Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeletePTBFFixationMutation, useLazyPrintPTBFFixationQuery, useListPTBFFixationsQuery,
  type PTBFFixationPrintKind,
} from '@/store/api/ptbfFixationsApi'
import type { PTBFFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { sumNumericColumn } from '@/utils/tableTotals'

import PTBFFixationFormDialog from './PTBFFixationFormDialog'

type FilterKind = 'search' | 'futuresMonth' | 'uploaded'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const PRINT_KINDS: Array<{ kind: PTBFFixationPrintKind; label: string }> = [
  { kind: 'main', label: 'In Fixation' },
  { kind: 'cancel', label: 'Print Cancel' },
  { kind: 'sub', label: 'Print Sub' },
]

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, textTransform: 'none', fontSize: '0.7rem', lineHeight: 1.1,
  '&.MuiButton-outlined': { borderColor: alpha(theme.palette.text.primary, 0.14), '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.24), backgroundColor: alpha(theme.palette.text.primary, 0.04) } },
})
const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'number' | 'bool'
type ColumnDef = { key: keyof PTBFFixationOut | '_quality_str'; label: string; kind?: ColumnKind; align?: 'right'; digits?: number }
const NON_SUM_TOTAL_KEYS = new Set<string>(['market_level', 'diffs', 'final_price'])

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'fixation_date', label: 'Date', kind: 'date' },
  { key: 'contract_no', label: 'Contract No' },
  { key: 'supplier_name', label: 'CP Name' },
  { key: '_quality_str', label: 'Quality' },
  { key: 'total_lot', label: 'Total Lot', kind: 'number', align: 'right', digits: 0 },
  { key: 'lot_fixed', label: 'Lot Fixed', kind: 'number', align: 'right', digits: 0 },
  { key: 'lot_to_be_fixed', label: 'Lot To Be Fixed', kind: 'number', align: 'right', digits: 0 },
  { key: 'market_level', label: 'Market Level', kind: 'number', align: 'right', digits: 4 },
  { key: 'futures_month', label: 'Futures' },
  { key: 'diffs', label: 'Diffs', kind: 'number', align: 'right', digits: 4 },
  { key: 'final_price', label: 'Final Price', kind: 'number', align: 'right', digits: 4 },
  { key: 'issued_date', label: 'Issued Date', kind: 'date' },
  { key: 'is_upload', label: 'Uploaded', kind: 'bool' },
  { key: 'note', label: 'Note' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const distinctValues = (rows: PTBFFixationOut[], key: keyof PTBFFixationOut): string[] => {
  const values = new Set<string>()
  for (const r of rows) { const v = r[key]; if (v == null) continue; const t = String(v).trim(); if (t) values.add(t) }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const rowMatchesSearch = (row: PTBFFixationOut, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  const fields: Array<keyof PTBFFixationOut> = ['contract_no', 'supplier_name', 'futures_month', 'note']
  return fields.some((k) => { const v = row[k]; return v != null && String(v).toLowerCase().includes(needle) })
}

const cellValue = (row: PTBFFixationOut, col: ColumnDef): string => {
  if (col.key === '_quality_str') return Array.isArray(row.quality_code) ? row.quality_code.join(', ') : ''
  const val = row[col.key as keyof PTBFFixationOut]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'bool') return val ? 'Yes' : 'No'
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  return String(val)
}

export function PTBFFixationListPage() {
  const { canEdit } = usePagePermission()
  const navigate = useNavigate()
  const snack = useSnackbar()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [futuresMonthFilter, setFuturesMonthFilter] = useState('')
  const [uploadedOnly, setUploadedOnly] = useState(false)

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || futuresMonthFilter || uploadedOnly)
  const clearAllFilters = () => { setSearchTerm(''); setFuturesMonthFilter(''); setUploadedOnly(false); setPage(0) }

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<PTBFFixationOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<PTBFFixationOut | null>(null)
  const [printAnchor, setPrintAnchor] = useState<{ el: HTMLElement; id: number } | null>(null)

  const { data, isLoading, isFetching, error } = useListPTBFFixationsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const [deleteFixation, { isLoading: deleting }] = useDeletePTBFFixationMutation()
  const [triggerPrint] = useLazyPrintPTBFFixationQuery()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row, searchTerm)) return false
      if (futuresMonthFilter && row.futures_month !== futuresMonthFilter) return false
      if (uploadedOnly && !row.is_upload) return false
      return true
    }), [baseRows, searchTerm, futuresMonthFilter, uploadedOnly])

  const rows = useMemo(() => rowsPerPage === -1 ? filteredRows : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredRows, page, rowsPerPage])

  const futuresMonthOptions = useMemo(() => distinctValues(baseRows, 'futures_month'), [baseRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter(
          (col) => col.kind === 'number' && typeof col.key === 'string' && !col.key.startsWith('_') && !NON_SUM_TOTAL_KEYS.has(String(col.key)),
        ).map((col) => [String(col.key), sumNumericColumn(filteredRows, col.key as keyof PTBFFixationOut)]),
      ),
    [filteredRows],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.ptbf_p_id === selectedId) ?? null, [baseRows, selectedId])

  const handleViewContract = () => { if (!selectedRow) return; navigate(`/purchase/local-p/${encodeURIComponent(selectedRow.contract_no)}`) }
  const handleEdit = () => { if (!selectedRow) return; setEditing(selectedRow) }
  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try { await deleteFixation(confirmDel.ptbf_p_id).unwrap(); snack.success('Đã xoá fixation'); setConfirmDel(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Xoá fixation thất bại')) }
  }
  const handlePrint = async (kind: PTBFFixationPrintKind) => {
    if (!printAnchor) return
    const id = printAnchor.id
    setPrintAnchor(null)
    try { await triggerPrint({ id, kind }).unwrap(); snack.info(`Yêu cầu in (${kind}) đã gửi`) }
    catch (err) { snack.error(getErrorMessage(err, 'In PDF thất bại')) }
  }

  const editDisabled = !selectedRow || !!selectedRow.is_upload

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          <Button size="small" variant={futuresMonthFilter ? 'contained' : 'outlined'} color={futuresMonthFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('futuresMonth', e)} sx={filterBarBtnSx}>Futures Month</Button>
          <Button size="small" variant={uploadedOnly ? 'contained' : 'outlined'} color={uploadedOnly ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('uploaded', e)} sx={filterBarBtnSx}>Uploaded</Button>
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={handleViewContract}>View HĐ</Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={editDisabled || !canEdit} onClick={handleEdit}>Edit</Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={editDisabled || !canEdit} onClick={() => selectedRow && setConfirmDel(selectedRow)}>Delete</Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={(e) => selectedRow && setPrintAnchor({ el: e.currentTarget, id: selectedRow.ptbf_p_id })}>Print</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load PTBF Fixations.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }} />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="Contract no, supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'futuresMonth' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Futures Month</InputLabel>
              <Select label="Futures Month" value={futuresMonthFilter} onChange={(e) => { setFuturesMonthFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}>
                <MenuItem value="">All / Clear</MenuItem>
                {futuresMonthOptions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'uploaded' && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={uploadedOnly} onChange={(e) => { setUploadedOnly(e.target.checked); setPage(0) }} />
              <Typography variant="body2">Chỉ đã upload</Typography>
            </Stack>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatNumberFixed(total, col.digits ?? 0)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: PTBFFixationOut) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 2200, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.ptbf_p_id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) setSelectedId(id) }} />
              },
            }}
          />
        </Box>
      </Box>

      <PTBFFixationFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <PTBFFixationFormDialog open={!!editing} mode="edit" initialValue={editing} onClose={() => setEditing(null)} />
      <Menu anchorEl={printAnchor?.el ?? null} open={!!printAnchor} onClose={() => setPrintAnchor(null)}>
        {PRINT_KINDS.map((k) => <MenuItem key={k.kind} onClick={() => void handlePrint(k.kind)}>{k.label}</MenuItem>)}
      </Menu>
      <ConfirmDialog open={!!confirmDel} title="Xoá fixation" message={confirmDel ? `Xoá fixation #${confirmDel.ptbf_p_id} của hợp đồng ${confirmDel.contract_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDel(null)} />
    </Box>
  )
}

export default PTBFFixationListPage
